/**
 * The public header's entries on the portal's way-in pages (Claude Design's copy of 13 Oct 2026, 20:33 UTC: the Sign up /
 * Log in screen wears the `.wh` header; `HDR_SOL`, `HDR_PROD`, `HDR_T`).
 *
 * The portal's copy of the public site's model (`apps/web-public/src/site/chrome.ts`, `HEADER_*`) — the apps never
 * import each other — with the same entries, order, icons and destinations, each a path of wetalk.io that
 * `usePublicSiteLink` turns into an address on the public site (under `/el/` for a Greek reader). Where the design and
 * the live site part, the public model says why, and this copy follows it: a "soon" entry and "Talk to us" go to the
 * Contact page (no waitlist is stored anywhere), Security & data goes to the Privacy page, the phone menu's Pricing line
 * names no figure, and the demo panel's Play goes to the landing page's demo. The words are the core catalog's
 * `site_header.*` phrases, English and Greek as the public site words them.
 */

/** Below this the bar gives way to the phone menu. Keep in step with `site-header.css`. */
export const HEADER_NARROW_QUERY = "(width < 64rem)";

/** The icons the phone menu's Solutions rows draw (Lucide's, as the design's `HDR_ICON_SEGS` spells them). */
export const HEADER_ICON = Object.freeze({
  utensils: ["M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2", "M7 2v20", "M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"],
  "shopping-bag": ["M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z", "M3 6h18", "M16 10a4 4 0 0 1-8 0"],
  "clipboard-list": [
    "M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z",
    "M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2",
    "M12 11h4",
    "M12 16h4",
    "M8 11h.01",
    "M8 16h.01",
  ],
  "clipboard-check": [
    "M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z",
    "M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2",
    "m9 14 2 2 4-4",
  ],
  "bed-double": ["M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8", "M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4", "M12 4v6", "M2 18h20"],
  stethoscope: [
    "M11 2v2",
    "M5 2v2",
    "M5 3H4a2 2 0 0 0-2 2v4a6 6 0 0 0 12 0V5a2 2 0 0 0-2-2h-1",
    "M8 15a6 6 0 0 0 12 0v-3",
    "M18 10a2 2 0 1 0 4 0a2 2 0 1 0-4 0",
  ],
  scissors: [
    "M3 6a3 3 0 1 0 6 0a3 3 0 1 0-6 0",
    "M8.12 8.12 12 12",
    "M20 4 8.12 15.88",
    "M3 18a3 3 0 1 0 6 0a3 3 0 1 0-6 0",
    "M14.8 14.8 20 20",
  ],
  receipt: [
    "M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z",
    "M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8",
    "M12 17.5v-11",
  ],
  headset: [
    "M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z",
    "M21 16v2a4 4 0 0 1-4 4h-5",
  ],
  "calendar-check": ["M8 2v4", "M16 2v4", "M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z", "M3 10h18", "m9 16 2 2 4-4"],
  bell: ["M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9", "M10.3 21a1.94 1.94 0 0 0 3.4 0"],
} as const satisfies Readonly<Record<string, readonly string[]>>);
export type HeaderIcon = keyof typeof HEADER_ICON;

/** Where a soon entry and "Talk to us" go: the public site's Contact page. */
export const HEADER_CONTACT_PATH = "/contact/";

/** The Solutions menu's entries, industries then use cases; each names its words in the core catalog. */
export type HeaderSolutionId =
  | "restaurants"
  | "retail"
  | "research"
  | "hotels"
  | "clinics"
  | "beauty"
  | "delivery"
  | "surveys"
  | "support"
  | "appointments"
  | "reminders";

/** One entry of the Solutions menu. Its words are `site_header.solution.<id>.name` and `….description`. */
export interface HeaderSolution {
  readonly id: HeaderSolutionId;
  readonly icon: HeaderIcon;
  readonly status: "live" | "soon";
  readonly path: string;
}

function solution(id: HeaderSolutionId, icon: HeaderIcon, page: string | null): HeaderSolution {
  return page === null ? { id, icon, status: "soon", path: HEADER_CONTACT_PATH } : { id, icon, status: "live", path: page };
}

/** The design's `HDR_SOL.industries`, live first, in its order. */
export const HEADER_INDUSTRY: readonly HeaderSolution[] = Object.freeze([
  solution("restaurants", "utensils", "/delivery/"),
  solution("retail", "shopping-bag", "/delivery/"),
  solution("research", "clipboard-list", "/surveys/"),
  solution("hotels", "bed-double", null),
  solution("clinics", "stethoscope", null),
  solution("beauty", "scissors", null),
]);

/**
 * The design's `HDR_SOL.useCases`, live first, in its order. AMENDED 2026-10-15 (Claude Design's copy of 14 Oct 2026
 * 21:50 UTC, as the public site's `HEADER_USE_CASE`): "Order taking" is "Delivery & ordering", and the soon "AI
 * receptionist" gives its place to the live "Customer support" (`/support/`), so the soon use cases are appointments
 * and reminders.
 */
export const HEADER_USE_CASE: readonly HeaderSolution[] = Object.freeze([
  solution("delivery", "receipt", "/delivery/"),
  solution("surveys", "clipboard-check", "/surveys/"),
  solution("support", "headset", "/support/"),
  solution("appointments", "calendar-check", null),
  solution("reminders", "bell", null),
]);

/** The Product and Resources menus' entries; each names its words in the core catalog. */
export type HeaderLinkId = "how" | "library" | "live" | "orders" | "channels" | "languages" | "integrations" | "security" | "docs" | "api" | "status" | "faq";

/** One entry of the Product and Resources menus. Its words are `site_header.link.<id>.name` and `….description`. */
export interface HeaderLink {
  readonly id: HeaderLinkId;
  readonly path: string;
}

/** The design's `HDR_PROD`, each pointed at the page the public site builds (or a section of its landing page). */
export const HEADER_LINK = Object.freeze({
  how: { id: "how", path: "/#how" },
  library: { id: "library", path: "/agents/" },
  liveView: { id: "live", path: "/live/" },
  orderBook: { id: "orders", path: "/orders/" },
  numbers: { id: "channels", path: "/channels/" },
  languages: { id: "languages", path: "/languages/" },
  integrations: { id: "integrations", path: "/developers/" },
  security: { id: "security", path: "/privacy/" },
  docs: { id: "docs", path: "/docs/start/" },
  api: { id: "api", path: "/docs/api/" },
  status: { id: "status", path: "/status/" },
  faq: { id: "faq", path: "/#faq" },
} as const satisfies Readonly<Record<string, HeaderLink>>);

/** The Product dropdown's two columns and the Resources dropdown, as the desktop bar orders them. */
export const HEADER_PLATFORM: readonly HeaderLink[] = Object.freeze([HEADER_LINK.how, HEADER_LINK.library, HEADER_LINK.liveView, HEADER_LINK.orderBook]);
export const HEADER_CONNECT: readonly HeaderLink[] = Object.freeze([HEADER_LINK.numbers, HEADER_LINK.languages, HEADER_LINK.integrations, HEADER_LINK.security]);
export const HEADER_RESOURCE: readonly HeaderLink[] = Object.freeze([HEADER_LINK.docs, HEADER_LINK.api, HEADER_LINK.status, HEADER_LINK.faq]);
/** The phone menu's Product screen, in the design's own (different) order. */
export const HEADER_PRODUCT_NARROW: readonly HeaderLink[] = Object.freeze([
  HEADER_LINK.how,
  HEADER_LINK.library,
  HEADER_LINK.numbers,
  HEADER_LINK.liveView,
  HEADER_LINK.orderBook,
  HEADER_LINK.languages,
  HEADER_LINK.integrations,
  HEADER_LINK.security,
]);

/** The bar's Pricing link, "See all agents", and the demo panel's link (the landing page's phone). */
export const HEADER_PRICING_PATH = "/pricing/";
export const HEADER_LIBRARY_PATH = "/agents/";
export const HEADER_DEMO_PATH = "/#demo";
